import React, { useState } from "react";
import { Highlight } from "../../model/Highlight";
import { API_URLS } from "../../App";
import api from "../../hooks/api";
import {
  Trash2,
  Edit,
  ExternalLink,
  Calendar,
  Eye,
  EyeOff,
} from "lucide-react";

interface ListHighlightItemsProps {
  data: Highlight[];
  handleManageItem: (highlight: Highlight) => void;
  handleDeleteItem: (highlight: Highlight) => void;
  setError: (error: string | null) => void;
}

const ListHighlightItems: React.FC<ListHighlightItemsProps> = ({
  data,
  handleManageItem,
  handleDeleteItem,
  setError,
}) => {
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const handleDelete = async (highlight: Highlight) => {
    if (
      !window.confirm(`Tem certeza que deseja apagar "${highlight.title}"?`)
    ) {
      return;
    }

    setDeletingId(highlight.id!);
    try {
      await api.delete(`${API_URLS.HIGHLIGHTS}/${highlight.id}`);
      handleDeleteItem(highlight);
    } catch (error: any) {
      console.error("Error deleting highlight:", error);
      setError(`Erro ao apagar destaque: ${error.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("pt-PT", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const isActive = (highlight: Highlight) => {
    const now = new Date();
    const beginDate = new Date(highlight.begin_date);
    const endDate = new Date(highlight.end_date);
    return highlight.is_active && now >= beginDate && now <= endDate;
  };

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
        <p className="text-gray-500">Nenhum destaque encontrado.</p>
        <p className="text-sm text-gray-400 mt-2">
          Clique em "Criar Destaque" para adicionar um novo.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Ordem
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Imagem
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Título
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Período
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Estado
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Link
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Ações
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((highlight) => (
            <tr key={highlight.id} className="hover:bg-gray-50 transition">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {highlight.display_order}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {highlight.images && highlight.images.length > 0 ? (
                  <div className="relative">
                    <img
                      src={highlight.images[0]}
                      alt={highlight.title}
                      className="h-12 w-12 object-cover rounded"
                    />
                    {highlight.images.length > 1 && (
                      <span className="absolute -bottom-1 -right-1 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {highlight.images.length}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="h-12 w-12 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">
                    Sem imagem
                  </div>
                )}
              </td>
              <td className="px-6 py-4">
                <div className="text-sm font-medium text-gray-900">
                  {highlight.title}
                </div>
                {highlight.description && (
                  <div className="text-sm text-gray-500 truncate max-w-xs">
                    {highlight.description.substring(0, 60)}
                    {highlight.description.length > 60 && "..."}
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(highlight.begin_date)}</span>
                </div>
                <div className="flex items-center gap-1 text-xs">
                  <span>até {formatDate(highlight.end_date)}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {isActive(highlight) ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <Eye className="w-3 h-3 mr-1" />
                    Ativo
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    <EyeOff className="w-3 h-3 mr-1" />
                    Inativo
                  </span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {highlight.compound_id && (
                  <span
                    className="text-blue-600"
                    title={`PC: ${highlight.compound_id}`}
                  >
                    Produto
                  </span>
                )}
                {highlight.link_url && (
                  <a
                    href={highlight.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Link externo
                  </a>
                )}
                {!highlight.compound_id && !highlight.link_url && (
                  <span className="text-gray-400">—</span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => handleManageItem(highlight)}
                    className="text-blue-600 hover:text-blue-900 p-1.5 hover:bg-blue-50 rounded transition"
                    title="Editar"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(highlight)}
                    disabled={deletingId === highlight.id}
                    className="text-red-600 hover:text-red-900 p-1.5 hover:bg-red-50 rounded transition disabled:opacity-50"
                    title="Apagar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ListHighlightItems;
